import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Layers, ArrowRight, Loader2, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, setToken } from "@/lib/api";

const inputCls = "w-full bg-[#0D0F18] border border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 outline-none rounded-xl px-3.5 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 transition-colors";

const Shell = ({ children }) => (
  <div className="min-h-full flex items-center justify-center relative overflow-hidden p-6" data-testid="password-page">
    <div className="absolute inset-0 grid-bg pointer-events-none" />
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-indigo-600/10 blur-[140px] pointer-events-none" />
    <div className="relative w-full max-w-md bg-[#11131E]/90 backdrop-blur-xl border border-indigo-500/20 shadow-2xl rounded-2xl p-8 rise">
      <Link to="/login" className="flex items-center gap-2.5 mb-8" data-testid="password-logo">
        <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center"><Layers className="w-4 h-4 text-white" /></div>
        <span className="font-display font-semibold text-slate-100">OmniModel</span>
      </Link>
      {children}
    </div>
  </div>
);

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      {sent ? (
        <div data-testid="forgot-sent">
          <MailCheck className="w-8 h-8 text-emerald-400 mb-4" />
          <h1 className="font-display text-xl sm:text-2xl font-semibold text-slate-100">Check your inbox</h1>
          <p className="mt-2 text-sm text-slate-400 leading-relaxed">If an account exists for <span className="text-slate-200">{email}</span>, we've sent a link to choose a new password. It's valid for 1 hour.</p>
          <Link to="/login" data-testid="forgot-back-login" className="mt-6 inline-flex items-center gap-2 text-sm text-indigo-300 hover:text-white">Back to sign in <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <h1 className="font-display text-xl sm:text-2xl font-semibold text-slate-100">Forgot your password?</h1>
          <p className="mt-2 mb-6 text-sm text-slate-400">Enter your email and we'll send you a reset link.</p>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Email</span>
            <input data-testid="forgot-email-input" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
          </label>
          {error && <p data-testid="forgot-error" className="mt-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}
          <button data-testid="forgot-submit-btn" disabled={busy} type="submit" className="mt-6 w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-xl transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
          </button>
          <Link to="/login" data-testid="forgot-cancel" className="mt-4 block text-center text-xs text-slate-400 hover:text-indigo-300">Back to sign in</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage({ onAuthed }) {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords don't match");
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setToken(data.token);
      onAuthed(data.user);
      toast.success("Password updated — you're signed in");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <form onSubmit={submit} data-testid="reset-form">
        <KeyRound className="w-8 h-8 text-indigo-400 mb-4" />
        <h1 className="font-display text-xl sm:text-2xl font-semibold text-slate-100">Choose a new password</h1>
        <p className="mt-2 mb-6 text-sm text-slate-400">At least 6 characters. You'll be signed in right after.</p>
        {!token && <p data-testid="reset-missing-token" className="mb-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">This link is missing its token. Please use the link from your email.</p>}
        <div className="space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">New password</span>
            <input data-testid="reset-password-input" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} required />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Confirm password</span>
            <input data-testid="reset-confirm-input" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={6} required />
          </label>
        </div>
        {error && <p data-testid="reset-error" className="mt-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">{error}</p>}
        <button data-testid="reset-submit-btn" disabled={busy || !token} type="submit" className="mt-6 w-full flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-60 text-white font-medium py-2.5 rounded-xl transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Update password <ArrowRight className="w-4 h-4" /></>}
        </button>
        <Link to="/forgot-password" data-testid="reset-request-new" className="mt-4 block text-center text-xs text-slate-400 hover:text-indigo-300">Request a new link</Link>
      </form>
    </Shell>
  );
}
